import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../api/endpoints'
import { useI18n } from '../i18n/I18nProvider'
import { Busy, ErrorBanner } from '../ui/controls'
import { Band } from '../ui/Band'
import type { Invitation, InviteeView } from '../api/types'

const INVITEE_UNLOCK_PRODUCT = 'MATCH_UNLOCK_INVITEE'

function when(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : ''
}

export function InvitationsPage() {
  const { t } = useI18n()

  const [incoming, setIncoming] = useState<Invitation[] | null>(null)
  const [outgoing, setOutgoing] = useState<Invitation[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const load = () => {
    api.invitations.incoming().then(setIncoming).catch(setError)
    api.invitations.outgoing().then(setOutgoing).catch(setError)
  }

  useEffect(load, [])

  if (!incoming || !outgoing) {
    return error ? <ErrorBanner error={error} /> : <Busy />
  }

  const act = (run: Promise<unknown>) => {
    setError(null)
    setBusy(true)

    run
      .then(load)
      .catch(setError)
      .finally(() => setBusy(false))
  }

  return (
    <div className="page stack">
      <h1>{t('invitations.title')}</h1>

      <ErrorBanner error={error} />
      <p className="field-hint">{t('invitations.sevenDays')}</p>

      <section className="card stack">
        <h2>{t('invitations.incoming')}</h2>

        {incoming.length === 0 ? (
          <p className="field-hint">{t('invitations.none')}</p>
        ) : (
          incoming.map((invitation) => (
            <div className="invite-row" key={invitation.invitationId}>
              <span className="field-hint">
                {t('invitations.expiresAt')} {when(invitation.expiresAt)}
              </span>

              <div className="row-links">
                {/* The free layer. Deciding starts by reading it, not by paying. */}
                <Link to={`/invitations/${invitation.invitationId}`}>{t('invitations.open')}</Link>

                <button
                  type="button"
                  className="link-button"
                  disabled={busy}
                  onClick={() => act(api.invitations.decline(invitation.invitationId))}
                >
                  {t('invitations.decline')}
                </button>
              </div>
            </div>
          ))
        )}

        {incoming.length > 0 ? <p className="field-hint">{t('invitations.declineNote')}</p> : null}
      </section>

      <section className="card stack">
        <h2>{t('invitations.outgoing')}</h2>

        {outgoing.length === 0 ? (
          <p className="field-hint">{t('invitations.none')}</p>
        ) : (
          outgoing.map((invitation) => (
            <div className="invite-row" key={invitation.invitationId}>
              <span className="field-hint">
                {t('invitations.expiresAt')} {when(invitation.expiresAt)}
              </span>

              <div className="row-links">
                <Link to={`/matches/${invitation.matchId}`}>{t('matches.viewReport')}</Link>

                <button
                  type="button"
                  className="link-button"
                  disabled={busy}
                  onClick={() => act(api.invitations.cancel(invitation.invitationId))}
                >
                  {t('invitations.cancel')}
                </button>
              </div>
            </div>
          ))
        )}

        {outgoing.length > 0 ? <p className="field-hint">{t('invitations.cancelNote')}</p> : null}
      </section>
    </div>
  )
}

/**
 * L1.5 — the only layer nobody paid for (决议 V1.2 §5.5).
 *
 * It carries evidence of fit and nothing of the person: no photo, no introduction, no occupation,
 * a nickname truncated to ten characters by the server, and the sender's note. **One direction
 * only** — how well the sender meets *this reader's* requirements, never the reverse
 * (Nai, 2026-09-18: 人都是以自己为中心的……免得伤自尊).
 */
export function InviteeViewPage() {
  const { t } = useI18n()
  const { invitationId = '' } = useParams()
  const navigate = useNavigate()

  const [view, setView] = useState<InviteeView | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    api.invitations.view(invitationId).then(setView).catch(setError)
  }, [invitationId])

  if (!view) {
    return error ? <ErrorBanner error={error} /> : <Busy />
  }

  const accept = () => {
    setError(null)
    setBusy(true)

    api.invitations
      .accept(invitationId)
      .then(() => navigate('/connection'))
      .catch((cause) => {
        setError(cause)
        setBusy(false)
      })
  }

  const payToSeeFirst = () => {
    setError(null)
    setBusy(true)

    api.orders
      .create(view.matchId, INVITEE_UNLOCK_PRODUCT, crypto.randomUUID())
      .then((order) => api.orders.paymentSession(order.orderId))
      .then((session) => window.location.assign(session.redirectUrl))
      .catch((cause) => {
        setError(cause)
        setBusy(false)
      })
  }

  return (
    <div className="page stack">
      <h1>{t('invitee.title')}</h1>

      <ErrorBanner error={error} />
      <p className="lede">{t('invitee.free')}</p>

      <section className="card stack">
        <div className="kv">
          <span>{t('invitee.senderFitsMe')}</span>
          <Band value={view.senderFitsMe} />
        </div>

        <div className="kv">
          <span>{t('invitations.expiresAt')}</span>
          <strong>{when(view.expiresAt)}</strong>
        </div>

        {view.itemFits.length > 0 ? (
          <ul className="fit-list">
            {view.itemFits.map((fit) => (
              <li key={fit.field}>
                <span>{fit.field}</span> <Band value={fit.band} />
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      {view.message ? (
        <section className="card">
          <p className="note">{view.message}</p>
        </section>
      ) : null}

      <section className="card stack">
        <button type="button" className="primary" disabled={busy} onClick={accept}>
          {t('invitee.acceptFree')}
        </button>

        <p className="field-hint">{t('invitee.acceptMeaning')}</p>

        {/*
          Path B. Offered only when the sender wrote enough for the upgrade to be worth its price
          (决议 §5.7 内容闸门) — the server decides that, not this screen. Accepting for free is
          never gated.
        */}
        {view.earlyUnlockOffered ? (
          <button type="button" className="link-button" disabled={busy} onClick={payToSeeFirst}>
            {t('invitee.earlyUnlock')}
          </button>
        ) : null}

        <button
          type="button"
          className="danger"
          disabled={busy}
          onClick={() => {
            setBusy(true)
            api.invitations
              .decline(invitationId)
              .then(() => navigate('/invitations'))
              .catch((cause) => {
                setError(cause)
                setBusy(false)
              })
          }}
        >
          {t('invitations.decline')}
        </button>

        <p className="field-hint">{t('invitations.declineNote')}</p>
      </section>
    </div>
  )
}
